<template>
    <div v-if="show && quotes && quotes.length" class="stock-sticky" :style="{ top: stickyTopCss }">
        <div class="stock-sticky-inner" ref="stockListContainer" :style="stockListStyle">
            <div class="stock-carousel-wrapper" ref="carouselWrapper" :style="carouselStyle">
                <StockListItem
                    v-for="(item, index) in quotes"
                    :key="`original-${item.code || item.rawSymbol || index}`"
                    :item="item"
                    :item-height="effectiveItemHeight"
                    :theme="theme"
                    :mkt-color="mktColor"
                    :is-app="isApp"
                    :is-t-h-s-app="isTHSApp"
                    :show-night="isShowStockNight(item)"
                    @click="$emit('go-quote-detail', item)"
                    @attention="$emit('attention', item)"
                />
                <StockListItem
                    v-for="(item, index) in duplicatedQuotes"
                    :key="`duplicated-${item.code || item.rawSymbol || index}`"
                    :item="item"
                    :item-height="effectiveItemHeight"
                    :theme="theme"
                    :mkt-color="mktColor"
                    :is-app="isApp"
                    :is-t-h-s-app="isTHSApp"
                    :show-night="isShowStockNight(item)"
                    @click="$emit('go-quote-detail', item)"
                    @attention="$emit('attention', item)"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import StockListItem from './StockListItem.vue'

export default {
    name: 'InformationDetailStickyHeader',
    components: { StockListItem },
    props: {
        show: { type: Boolean, required: true },
        quotes: { type: Array, default: () => [] },
        currentIndex: { type: Number, default: 0 },
        stickyTopCss: { type: String, required: true },
        theme: { type: String, required: true },
        mktColor: { type: Object, required: true },
        isApp: { type: Boolean, required: true },
        isTHSApp: { type: Function, required: true },
    },
    emits: ['attention', 'go-quote-detail'],
    setup(props) {
        const stockListContainer = ref(null)
        const carouselWrapper = ref(null)
        const itemHeight = ref(60)
        const effectiveItemHeight = computed(() => Math.round(itemHeight.value || 0))
        const carouselOffset = ref(0)
        const isTransitioning = ref(false)

        const duplicatedQuotes = computed(() => {
            if (!props.quotes || props.quotes.length < 2) return []
            return props.quotes.slice(0, 1) // Just 1 item for duplication is enough
        })

        const stockListStyle = computed(() => ({
            height: `${effectiveItemHeight.value}px`
        }))

        const carouselStyle = computed(() => {
            const transformValue = `translate3d(0, -${Math.round(carouselOffset.value)}px, 0)`
            return {
                transform: transformValue,
                WebkitTransform: transformValue,
                msTransform: transformValue,
                transition: isTransitioning.value ? 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
            }
        })

        const updateHeight = () => {
            const root = carouselWrapper.value || stockListContainer.value
            if (!root) return
            const nodes = root.querySelectorAll('.stock-list-item')
            if (!nodes || !nodes.length) return
            let maxH = 0
            for (let i = 0; i < nodes.length; i++) {
                const el = nodes[i]
                const h = el && el.offsetHeight ? el.offsetHeight : 0
                if (h > maxH) maxH = h
            }
            if (maxH > 0) itemHeight.value = maxH
        }

        const updateOffset = (index, animate = true) => {
            isTransitioning.value = animate
            carouselOffset.value = index * effectiveItemHeight.value
        }

        onMounted(() => {
            nextTick(() => {
                updateHeight()
                updateOffset(props.currentIndex, false)
            })
        })

        watch(() => props.quotes, () => {
            nextTick(() => {
                updateHeight()
                updateOffset(props.currentIndex, false)
            })
        }, { deep: true })

        watch(() => props.show, (val) => {
            if (val) {
                nextTick(() => {
                    updateHeight()
                    updateOffset(props.currentIndex, false)
                })
            }
        })

        watch(() => props.currentIndex, (newVal, oldVal) => {
            if (!props.show) return
            const count = props.quotes?.length || 0
            if (count <= 1) {
                updateOffset(newVal, false)
                return
            }
            // When parent wraps from last -> first, animate to duplicated item then snap to 0
            if (oldVal === count - 1 && newVal === 0) {
                isTransitioning.value = true
                carouselOffset.value = count * effectiveItemHeight.value
                const wrapper = carouselWrapper.value
                if (wrapper) {
                    const onEnd = (e) => {
                        if (!e || e.propertyName !== 'transform') return
                        wrapper.removeEventListener('transitionend', onEnd)
                        isTransitioning.value = false
                        carouselOffset.value = 0
                    }
                    wrapper.addEventListener('transitionend', onEnd)
                } else {
                    // Fallback if no wrapper ref
                    isTransitioning.value = false
                    carouselOffset.value = 0
                }
            } else {
                // Normal smooth scroll
                isTransitioning.value = true
                carouselOffset.value = newVal * effectiveItemHeight.value
                const wrapper = carouselWrapper.value
                if (wrapper) {
                    const onEnd = (e) => {
                        if (!e || e.propertyName !== 'transform') return
                        wrapper.removeEventListener('transitionend', onEnd)
                        isTransitioning.value = false
                        const style = window.getComputedStyle(wrapper)
                        const tf = style.transform || style.WebkitTransform || style.msTransform
                        let ty = 0
                        if (tf && tf.startsWith('matrix3d')) {
                            const vals = tf.replace('matrix3d(', '').replace(')', '').split(',')
                            ty = parseFloat(vals[13] || '0')
                        } else if (tf && tf.startsWith('matrix')) {
                            const vals = tf.replace('matrix(', '').replace(')', '').split(',')
                            ty = parseFloat(vals[5] || '0')
                        }
                        const current = Math.abs(ty)
                        const step = effectiveItemHeight.value
                        const snapped = Math.round(current / step) * step
                        carouselOffset.value = snapped
                    }
                    wrapper.addEventListener('transitionend', onEnd)
                } else {
                    isTransitioning.value = false
                }
            }
        })

        const isShowStockNight = (item) => {
            return !!(item?.nightTrading && item?.nightQuote?.price)
        }

        return {
            stockListContainer,
            carouselWrapper,
            stockListStyle,
            carouselStyle,
            effectiveItemHeight,
            duplicatedQuotes,
            isShowStockNight
        }
    }
}
</script>

<style scoped lang="less">
@import url('~@/assets/css/mixins.less');
@import url('../css/mixins.less');

.stock-sticky {
    position: fixed;
    top: var(--nav-header-height);
    left: 0;
    right: 0;
    z-index: 100;
    transform: translateY(0);
    transition: transform 0.2s ease, opacity 0.2s ease;
    touch-action: pan-y;
}

.stock-sticky-inner {
    padding: 0 16px;
    overflow: hidden;
    position: relative;
    box-sizing: content-box;
    
    .stock-carousel-wrapper {
        position: relative;
        transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        will-change: transform;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
        transform-style: preserve-3d;
        display: block;
        transform-origin: top center;
    }

    .stock-list-item {
        #news_pages_background();
    }
}

.sticky-sentinel {
    width: 100%;
    height: 1px;
}
</style>
